import type { MeView } from '@research-assistant/shared';
import { BugIcon } from 'lucide-react';
import { useRef, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import type { SupportTicketContext } from '@/lib/api/support.js';
import {
  createSupportTicket,
  useSupportTicketsEnabled,
} from '@/lib/api/support.js';
import { buildBugReport, buildReportContext } from './bug-report.js';

const TOOLTIP_LABEL = 'Found a bug? Tell us about it';

export interface BugReportButtonProps {
  identity: MeView | null;
  workspaceId: string | null;
  conversationId: string | null;
  /** The chat answer the report is about, when it is sent from one. */
  messageId?: string;
  /** The error line that answer showed. When set, the form opens on a draft that quotes it. */
  errorText?: string;
  renderTrigger?: (open: () => void) => JSX.Element;
}

/**
 * The draft a report from a failed answer opens on. It quotes the error the user saw and leaves
 * room for what they were doing, which is the one fact the ids and the error cannot tell us.
 * Nothing is sent until the user has read it and pressed Send.
 */
/** The longest error text the support service accepts on `context.error_text`. */
const MAX_ERROR_TEXT = 2000;

/**
 * The error text cut to what the support service accepts. A longer one would get the whole ticket
 * refused, and would crowd the user's own words out of the 5,000-character form.
 */
function cappedErrorText(errorText?: string): string | undefined {
  if (!errorText || errorText.length <= MAX_ERROR_TEXT) return errorText;
  return `${errorText.slice(0, MAX_ERROR_TEXT - 1)}…`;
}

function errorDraft(errorText: string): string {
  return `What went wrong:\n"${errorText}"\n\nWhat I was trying to do:\n`;
}

/**
 * Writes the report to the clipboard, reporting whether it landed. The clipboard
 * is genuinely unavailable in some hosts we run in (an insecure origin, a
 * restricted Teams webview), and the dialog tells the user which case they are
 * in — so this returns a boolean rather than throwing.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * The bug-report widget: a small icon parked in the bottom-right corner of every
 * signed-in page. One click opens a short form; sending it files a Jira Service
 * Desk ticket raised for the person reporting, with the page URL, the browser,
 * the OS and the ids a developer needs already attached.
 *
 * The clipboard copy stays available when Jira filing is not configured. The
 * form only appears where the server says filing works, so nobody is offered a
 * button that always fails.
 *
 * Positioned `absolute`, so the caller anchors it to the bottom-right of the
 * content column rather than of the viewport. Viewport-fixed would paint it over
 * the artifact rail, which is a real layout column in that same corner.
 */
export function BugReportButton({
  identity,
  workspaceId,
  conversationId,
  messageId,
  errorText: fullErrorText,
  renderTrigger,
}: BugReportButtonProps): JSX.Element {
  const errorText = cappedErrorText(fullErrorText);
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState('');
  const [reportContext, setReportContext] =
    useState<SupportTicketContext | null>(null);
  const [copied, setCopied] = useState(false);
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [filed, setFiled] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * Which report a pending send belongs to. The close button stays live while a ticket is filing,
   * so a slow first request could otherwise resolve against a second, freshly opened report and
   * confirm something that report never sent.
   */
  const submission = useRef(0);
  const canFileTickets = useSupportTicketsEnabled();

  const reportSubject = {
    identity,
    workspaceId,
    conversationId,
    messageId,
    errorText,
  };

  async function collectAndOpen(): Promise<void> {
    const text = buildBugReport(reportSubject);
    setReport(text);
    /**
     * Captured with the clipboard copy, from the same moment. Rebuilding it on send would let the
     * two describe different screens whenever the page moved under an open dialog.
     */
    setReportContext(buildReportContext(reportSubject));
    setCopied(await copyToClipboard(text));
    setDescription(errorText ? errorDraft(errorText) : '');
    setFiled(false);
    setFailure(null);
    setSending(false);
    submission.current += 1;
    setOpen(true);
  }

  async function submit(): Promise<void> {
    const sent = submission.current;
    setSending(true);
    setFailure(null);
    try {
      await createSupportTicket({
        kind: 'bug',
        description: description.trim(),
        context: reportContext ?? buildReportContext(reportSubject),
      });
      if (sent === submission.current) setFiled(true);
    } catch (error) {
      if (sent === submission.current) {
        setFailure(
          error instanceof Error
            ? error.message
            : 'Could not reach the server.',
        );
      }
    } finally {
      if (sent === submission.current) setSending(false);
    }
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => void collectAndOpen())
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={TOOLTIP_LABEL}
              onClick={() => void collectAndOpen()}
              className="absolute bottom-3 right-3 z-30 rounded-full text-muted-foreground/50 opacity-70 transition-opacity hover:bg-muted hover:text-foreground hover:opacity-100 focus-visible:opacity-100"
            >
              <BugIcon className="size-4" aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{TOOLTIP_LABEL}</TooltipContent>
        </Tooltip>
      )}

      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title="Report a bug"
        description={
          filed
            ? undefined
            : canFileTickets
              ? 'Tell us what went wrong and we will open a ticket for you. The page you were on, your browser and the ids that identify this session are attached automatically.'
              : copied
                ? 'Everything a developer needs is already on your clipboard — the page you were on, your browser, and the ids that identify this session.'
                : 'Your browser would not let us reach the clipboard, so copy the details below by hand.'
        }
        className="sm:max-w-md"
      >
        <div className="flex flex-col gap-3">
          {canFileTickets && !filed && (
            <>
              <label className="flex flex-col gap-1.5 text-mini font-semibold uppercase tracking-wider text-muted-foreground">
                What happened
                <textarea
                  value={description}
                  rows={5}
                  maxLength={5000}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="What you were doing, what you expected, what you got instead"
                  className="w-full resize-none rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground/30 focus:bg-card focus:ring-2 focus:ring-foreground/10"
                />
              </label>
              {failure !== null && (
                <p role="alert" className="text-sm text-destructive">
                  {failure}
                </p>
              )}
              <Button
                variant="default"
                size="sm"
                className="self-start"
                disabled={sending || description.trim() === ''}
                onClick={() => void submit()}
              >
                {sending ? 'Sending…' : 'Send report'}
              </Button>
            </>
          )}

          {filed && (
            <p role="status" className="text-sm text-foreground">
              Thanks, we passed this to the team.
            </p>
          )}

          <pre className="max-h-52 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-xs text-muted-foreground">
            {report}
          </pre>
          {!copied && (
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => void copyToClipboard(report).then(setCopied)}
            >
              Copy again
            </Button>
          )}
        </div>
      </AppDialog>
    </>
  );
}
